import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import * as Router from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { LoadingIndicator } from "component/shared/loading-indicator";
import { Select } from "component/shared/select";
import { Table } from "component/shared/table";
import { Dialog } from "component/shared/dialog";
import { BoardOption } from "api/resource/client-board";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import {
	fetchClientBoards,
	updateClientBoard,
	addClientBoard,
	fetchClientBoardOptions,
} from "redux/slice/client-boards";
import { ClientBoardResource } from "api/resource/client-board";
import { coldFusionReplatform } from "shared/env";
import * as paths from "routes/paths";
import { mlsSetupMovesToTab } from "component/page/featured-mls-ids/access";
import { styles } from "./style";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {}

const title = "MLS Setup";

const ColdFusionFallback: React.FC<Props> = ({ user }) => (
	<DashboardLayout
		permitted={user && (!user.permissions.oneCrm || user.admin)}
		title={title}
		header={
			<Mui.Typography variant="h1">
				<FeatherIcon>
					<Icons.Settings />
				</FeatherIcon>
				{title}
			</Mui.Typography>
		}
		iframe={<ColdFusionIframe url="/mlsSetup.cfm" />}
	/>
);

const SettingsMlsBase: React.FC<Props> = ({ user, classes = {} }) => {
	const dispatch = useDispatch();
	const byId = useSelector((state: RootState) => state.clientBoards.byId);
	const loading = useSelector((state: RootState) => state.clientBoards.loading);
	const officeAccount = useSelector((state: RootState) => state.clientBoards.officeAccount);
	const options = useSelector((state: RootState) => state.clientBoards.options);

	// Office/MultiOffice accounts collapse to a single "MLS ID" field (D13).
	const agentIdLabel = officeAccount ? "MLS ID" : "Agent ID";

	const [boardId, setBoardId] = useState<number | "">("");
	const [agentId, setAgentId] = useState("");
	const [officeId, setOfficeId] = useState("");

	const [editingId, setEditingId] = useState<number | null>(null);
	const [editAgentId, setEditAgentId] = useState("");
	const [editOfficeId, setEditOfficeId] = useState("");

	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

	useEffect(() => {
		dispatch(fetchClientBoards() as any);
		dispatch(fetchClientBoardOptions() as any);
	}, [dispatch]);

	// Available boards the client may enter IDs for (provisioned-only). Server order is
	// BoardID ascending, matching CF board_dropdown.cfm:68 — don't re-sort.
	const sortedOptions = options;

	// D15: show only approved boards (CF `approvedOnlyYN=1`); pending rows stay in the
	// store for the options/add flow but are not listed. Default sort: board name asc.
	const rows = useMemo(
		() =>
			Object.values(byId)
				.filter((row) => row.status === "APPROVED")
				.sort((a, b) => a.boardName.localeCompare(b.boardName)),
		[byId]
	);

	const notify = (message: string, severity: "success" | "error") => {
		setSnackbarMessage(message);
		setSnackbarSeverity(severity);
		setSnackbarOpen(true);
	};

	const resetAddForm = () => {
		setBoardId("");
		setAgentId("");
		setOfficeId("");
	};

	// CF "Add Profile" (mlssetup_output_1.cfm / mlssetup_update.cfm): the picker lists all boards the
	// client is provisioned for, and Add inserts a NEW profile row (POST) for the chosen board with
	// the entered IDs. It never edits an existing row, so it can't overwrite another profile —
	// multiple MLS IDs per board are supported, matching CF. Existing rows are changed only via Modify.
	const handleAdd = async () => {
		const option = boardId === "" ? undefined : options.find((o) => o.boardId === boardId);
		if (!option) {
			notify("Select a board", "error");
			return;
		}
		try {
			await dispatch(addClientBoard({
				clientBoard: {
					// id is server-assigned on create; the POST ignores this placeholder.
					id: 0,
					boardId: option.boardId,
					boardName: `${option.shortName} - ${option.region}`,
					agentId,
					officeId,
				},
			}) as any).unwrap();
			notify("Board added", "success");
			resetAddForm();
		} catch(error) {
			// CF-parity: a 409 means the board is a duplicate / not provisioned (mls-setup.md:102).
			const status = error && (error as { status?: number }).status;
			notify(status === 409 ? "Board already added" : "Failed to add board", "error");
		}
	};

	const startEdit = (row: ClientBoardResource) => {
		setEditingId(row.id);
		setEditAgentId(row.agentId);
		setEditOfficeId(row.officeId);
	};

	const cancelEdit = () => {
		setEditingId(null);
		setEditAgentId("");
		setEditOfficeId("");
	};

	const handleSaveEdit = async (row: ClientBoardResource) => {
		try {
			await dispatch(updateClientBoard({
				clientBoard: { ...row, agentId: editAgentId, officeId: editOfficeId },
			}) as any).unwrap();
			notify("Board updated", "success");
			cancelEdit();
		} catch{
			notify("Failed to update board", "error");
		}
	};


	const columns = [
		{
			id: "boardName",
			title: "MLS Board",
			value: (row: ClientBoardResource) => row.boardName,
			sort: (a: ClientBoardResource, b: ClientBoardResource) =>
				a.boardName.localeCompare(b.boardName),
		},
		{
			id: "agentId",
			title: agentIdLabel,
			value: (row: ClientBoardResource) => (
				editingId === row.id ? (
					<Mui.TextField
						className={classes.idInput}
						variant="outlined"
						size="small"
						value={editAgentId}
						inputProps={{ maxLength: 250 }}
						onChange={(e) => setEditAgentId(e.target.value)}
					/>
				) : row.agentId
			),
			sort: (a: ClientBoardResource, b: ClientBoardResource) =>
				(a.agentId || "").localeCompare(b.agentId || ""),
		},
		{
			id: "officeId",
			title: "Office ID",
			// D13: Office ID is inert for Office/MultiOffice accounts — hide the column.
			hidden: officeAccount,
			value: (row: ClientBoardResource) => (
				editingId === row.id ? (
					<Mui.TextField
						className={classes.idInput}
						variant="outlined"
						size="small"
						value={editOfficeId}
						inputProps={{ maxLength: 250 }}
						onChange={(e) => setEditOfficeId(e.target.value)}
					/>
				) : row.officeId
			),
			sort: (a: ClientBoardResource, b: ClientBoardResource) =>
				(a.officeId || "").localeCompare(b.officeId || ""),
		},
		{
			id: "action",
			title: "Action",
			width: 140,
			value: (row: ClientBoardResource) => (
				editingId === row.id ? (
					<>
						<Mui.Button size="small" color="primary" onClick={() => handleSaveEdit(row)}>Save</Mui.Button>
						<Mui.Button size="small" onClick={cancelEdit}>Cancel</Mui.Button>
					</>
				) : (
					<Mui.Link component="button" type="button" onClick={() => startEdit(row)}>
						Modify
					</Mui.Link>
				)
			),
		},
	];

	return (
		<DashboardLayout
			permitted={user && (!user.permissions.oneCrm || user.admin)}
			title={title}
			header={<Mui.Typography variant="h1">{title}</Mui.Typography>}
		>
			{loading ? (
				<LoadingIndicator />
			) : (
			<FullPageCard>
				<Mui.Typography variant="body2" className={classes.intro}>
					Select an MLS board your account is provisioned for and record your {officeAccount ? "MLS ID" : "Agent ID and Office ID"}.
				</Mui.Typography>

				<Mui.Typography variant="h6" className={classes.sectionTitle}>MLS Information</Mui.Typography>

				<Mui.Box className={classes.addForm}>
					<Select
						className={classes.boardInput}
						variant="outlined"
						label="MLS Board"
						placeholder="--- Select a Board ---"
						value={boardId}
						options={sortedOptions}
						valueExtractor={(option: BoardOption) => option.boardId}
						labelExtractor={(option: BoardOption) => option.label}
						optionRenderer={(option: BoardOption) => <Mui.ListItemText primary={option.label} />}
						onChange={(value) => setBoardId(value === "" || value == null ? "" : Number(value))}
					/>

					<Mui.TextField
						className={classes.idInput}
						variant="outlined"
						size="small"
						label={agentIdLabel}
						value={agentId}
						inputProps={{ maxLength: 250 }}
						onChange={(e) => setAgentId(e.target.value)}
					/>

					{!officeAccount && (
						<Mui.TextField
							className={classes.idInput}
							variant="outlined"
							size="small"
							label="Office ID"
							value={officeId}
							inputProps={{ maxLength: 250 }}
							onChange={(e) => setOfficeId(e.target.value)}
						/>
					)}

					<Mui.Button
						className={classes.addButton}
						variant="contained"
						color="primary"
						disabled={boardId === ""}
						onClick={handleAdd}
					>
						{officeAccount ? "Add Profile" : "Add"}
					</Mui.Button>
				</Mui.Box>

				<Mui.Typography variant="h6" className={classes.sectionTitle}>MLS Records</Mui.Typography>

				{rows.length === 0 ? (
					<Mui.Typography variant="body2" color="textSecondary" className={classes.emptyState}>
						No MLS boards have been added yet.
					</Mui.Typography>
				) : (
					<Table items={rows} columns={columns} hidePagination />
				)}
			</FullPageCard>
			)}


			<Mui.Snackbar
				open={snackbarOpen}
				autoHideDuration={4000}
				onClose={() => setSnackbarOpen(false)}
			>
				<MuiAlert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
					{snackbarMessage}
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const SettingsMls = withStyles(SettingsMlsBase, styles);

const SettingsMlsPageBase: React.FC<Props> = (props) => {
	// With the MLS Setup redesign on, the MLS IDs live on the Featured Listings page's MLS IDs tab.
	if (props.user && mlsSetupMovesToTab(props.user)) {
		return <Router.Redirect to={paths.settingsListingResults} />;
	}
	if (!coldFusionReplatform) {
		return <ColdFusionFallback {...props} />;
	}
	return <SettingsMls {...props} />;
};

export const SettingsMlsPage = withStyles(SettingsMlsPageBase, styles);
